import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, Input, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { SubmitError } from '@/app/components/SubmitError';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { createDocument, listDocuments, setDocumentStatus } from '@/features/documents/api';
import { statusTone } from '@/features/documents/statusTone';
import {
  AUDIENCES,
  CATEGORIES,
  type Document,
  type DocumentAudience,
  type DocumentCategory,
} from '@/features/documents/types';
import { useMe } from '@/features/me/MeContext';
import { Toggle } from '@/features/settings/Toggle';
import { uploadCompanyFile } from '@/features/uploads/api';
import { DOCUMENT_EXTENSIONS, fileValidationError } from '@/features/uploads/validate';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/**
 * `/documents` — the Content pill's Documenten library (PLAN-FASE3.md
 * WP-03): an upload modal (title, category, audience, `file_url` — now
 * filled via a real file picker that uploads through
 * `POST /companies/{id}/uploads` (PLAN-FASE3.md WP-07) — and a
 * leesbevestiging-vereist toggle) plus a library list (published/archived,
 * total views). Same company-context and permission shape as
 * `CommunicationsPage`: no active company means nothing to upload or list,
 * and `companies.documents.*` is company-scoped only
 * (`permissions.ts`'s `canSeeDocumentsSection`), so a coach alone gets the
 * "not allowed" state.
 */
export function DocumentsPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();

  if (company === null) {
    return (
      <section className="page">
        <PageHeader title={t('documents.page.title')} />
        <p className="text-ink text-sm">{t('documents.page.noScope')}</p>
      </section>
    );
  }

  if (!permissions.canSeeDocumentsSection) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('documents.page.title')} />
        <EmptyState text={t('documents.page.forbidden')} />
      </section>
    );
  }

  return <CompanyDocuments companyId={company.id} companyName={company.name} />;
}

function CompanyDocuments({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation();
  const documents = useAsync(() => listDocuments(companyId), [companyId]);
  const [uploading, setUploading] = useState(false);

  const totalViews = documents.data?.reduce((sum, doc) => sum + (doc.views_count ?? 0), 0) ?? 0;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('documents.page.title')}
        lead={t('documents.page.lead', { company: companyName })}
        actions={
          <Button size="sm" onClick={() => setUploading((v) => !v)}>
            {uploading ? t('documents.actions.cancel') : t('documents.actions.new')}
          </Button>
        }
      />

      {uploading && (
        <UploadModal
          companyId={companyId}
          onDone={() => {
            setUploading(false);
            documents.reload();
          }}
        />
      )}

      <Card padding="none" aria-label={t('documents.library.title')}>
        <div className="p-5 pb-0">
          <CardHeader title={t('documents.library.title')} />
          <p className="text-ink/70 -mt-2 mb-2 text-[13px]">
            {t('documents.library.totalViews', { count: totalViews })}
          </p>
        </div>
        <AsyncSection state={documents} label={t('documents.library.title')} skeleton="table">
          {(rows) =>
            rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('documents.library.empty')} />
              </div>
            ) : (
              <div className="p-5 pt-0">
                <Library rows={rows} onChanged={documents.reload} />
              </div>
            )
          }
        </AsyncSection>
      </Card>
    </section>
  );
}

function Library({ rows, onChanged }: { rows: Document[]; onChanged: () => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<Error | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <DataTable
        rowKey={(row) => row.id ?? row.title ?? ''}
        columns={[
          {
            key: 'title',
            header: t('documents.library.columns.title'),
            render: (row) => <span className="text-ink text-[15px] font-medium">{row.title}</span>,
          },
          {
            key: 'category',
            header: t('documents.library.columns.category'),
            render: (row) => (row.category ? t(`documents.categories.${row.category}`) : '–'),
          },
          {
            key: 'views',
            header: t('documents.library.columns.views'),
            render: (row) => row.views_count ?? 0,
          },
          {
            key: 'status',
            header: t('documents.library.columns.status'),
            align: 'end',
            render: (row) =>
              row.status && row.id ? (
                <StatusCell
                  documentId={row.id}
                  companyId={row.company_id ?? ''}
                  status={row.status}
                  onChanged={onChanged}
                  onError={setError}
                />
              ) : null,
          },
        ]}
        rows={rows}
      />
      <SubmitError error={error} />
    </div>
  );
}

function StatusCell({
  documentId,
  companyId,
  status,
  onChanged,
  onError,
}: {
  documentId: string;
  companyId: string;
  status: NonNullable<Document['status']>;
  onChanged: () => void;
  onError: (error: Error | null) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function move(next: 'gepubliceerd' | 'archief') {
    setBusy(true);
    onError(null);
    try {
      await setDocumentStatus(companyId, documentId, next);
      onChanged();
    } catch (cause) {
      onError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <Badge tone={statusTone(status)}>{t(`documents.status.${status}`)}</Badge>
      {status === 'gepubliceerd' ? (
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void move('archief')}>
          {t('documents.actions.archive')}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void move('gepubliceerd')}
        >
          {t('documents.actions.publish')}
        </Button>
      )}
    </div>
  );
}

function UploadModal({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('algemeen');
  const [audience, setAudience] = useState<DocumentAudience>('iedereen');
  const [fileUrl, setFileUrl] = useState('');
  const [requiresReadConfirmation, setRequiresReadConfirmation] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<Error | null>(null);

  async function handleFile(file: File) {
    setUploadError(null);
    const validationError = fileValidationError(file, DOCUMENT_EXTENSIONS);
    if (validationError === 'tooLarge') {
      setUploadError(new Error(t('documents.upload.upload_status.tooLarge')));
      return;
    }
    if (validationError === 'invalidType') {
      setUploadError(new Error(t('documents.upload.upload_status.invalidType')));
      return;
    }
    setUploading(true);
    try {
      const result = await uploadCompanyFile(companyId, file);
      setFileUrl(result.url);
    } catch (cause) {
      setUploadError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setUploading(false);
    }
  }

  const form = useSubmit(async () => {
    await createDocument(companyId, {
      title,
      category,
      target_audience: audience,
      file_url: fileUrl,
      requires_read_confirmation: requiresReadConfirmation,
    });
    onDone();
  });

  return (
    <Card aria-label={t('documents.upload.title')}>
      <CardHeader title={t('documents.upload.title')} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.submit();
        }}
      >
        <Field label={t('documents.upload.titleField')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('documents.upload.category')}>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`documents.categories.${c}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('documents.upload.audience')}>
            <Select
              value={audience}
              onChange={(e) => setAudience(e.target.value as DocumentAudience)}
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {t(`documents.audiences.${a}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label={t('documents.upload.file')}
          hint={uploading ? t('documents.upload.upload_status.uploading') : undefined}
          error={uploadError?.message}
        >
          <input
            type="file"
            accept={DOCUMENT_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
            className="text-sm"
          />
        </Field>
        <Field label={t('documents.upload.fileUrl')} hint={t('documents.upload.fileUrlHint')}>
          <Input
            type="url"
            value={fileUrl}
            onChange={(e) => setFileUrl(e.target.value)}
            placeholder="https://…"
            required
          />
        </Field>
        <Toggle
          label={t('documents.upload.requiresReadConfirmation')}
          hint={t('documents.upload.requiresReadConfirmationHint')}
          checked={requiresReadConfirmation}
          onChange={setRequiresReadConfirmation}
        />
        <div className="flex gap-2">
          <Button type="submit" disabled={form.pending}>
            {t('documents.upload.submit')}
          </Button>
        </div>
        <SubmitError error={form.error} />
      </form>
    </Card>
  );
}
